/**
 * Agent Capacity Page — shadcn/ui redesign
 *
 * Visual changes only — all state, API calls, and handlers are preserved exactly.
 * Replaced: MUI (Box/Grid/Card/Table/Dialog/Chip/LinearProgress/Select/Avatar/Tooltip)
 *           → shadcn Card / Table / Sheet / DropdownMenu / Badge / Select / Input / Switch / Label
 * Added:    KPI strip (@container/card gradient cards with CardAction icon-circle),
 *           Team Health segmented bar (client-side derived),
 *           client-side search + status filter in CardAction,
 *           hover-reveal row actions (status DropdownMenu + settings Sheet trigger),
 *           Sheet for capacity settings (mini-KPI cards + form + toggles),
 *           skeleton loading rows, empty state, pagination, toast feedback.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Users2,
  UserCheck,
  Layers,
  Activity,
  AlertTriangle,
  Settings2,
  Search,
  RefreshCw,
  Scale,
  MessageSquare,
  Clock,
  ChevronDown,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../components/ui/use-toast';
import { cn } from '../lib/utils';
import api from '../services/api';

// ─── Interfaces (unchanged) ───────────────────────────────────────────────────

interface AgentWorkload {
  user_id: number;
  user_name: string;
  user_email: string;
  active_conversations: number;
  pending_conversations: number;
  resolved_today: number;
  conversations_by_channel: Record<string, number>;
  avg_response_time_minutes: number;
  load_score: number;
  status: string;
  is_available: boolean;
  max_conversations: number;
}

interface AgentCapacity {
  id: number;
  user_id: number;
  max_conversations: number;
  max_conversations_per_channel: Record<string, number>;
  auto_assignment_enabled: boolean;
  auto_assignment_priority: number;
  working_hours_enabled: boolean;
  working_hours: Record<string, { start: string; end: string }>;
  timezone: string;
  is_available: boolean;
  status: string;
  status_message?: string;
  current_workload?: {
    active_conversations: number;
    pending_conversations: number;
    resolved_today: number;
    load_score: number;
  };
}

interface WorkloadSummary {
  total_agents: number;
  available_agents: number;
  total_capacity: number;
  total_active_conversations: number;
  utilization_rate: number;
  average_load_per_agent: number;
}

// ─── Status / load config ─────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  { label: string; dotClass: string; badgeClass: string }
> = {
  available:  { label: 'Available',  dotClass: 'bg-emerald-500',         badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
  busy:       { label: 'Busy',       dotClass: 'bg-amber-400',           badgeClass: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  away:       { label: 'Away',       dotClass: 'bg-orange-400',          badgeClass: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300' },
  offline:    { label: 'Offline',    dotClass: 'bg-muted-foreground/50', badgeClass: 'border-border text-muted-foreground' },
  overloaded: { label: 'Overloaded', dotClass: 'bg-red-500',             badgeClass: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300' },
  high_load:  { label: 'High Load',  dotClass: 'bg-amber-500',           badgeClass: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300' },
};

const getEffectiveStatus = (w: AgentWorkload): string => {
  if (w.status === 'offline') return 'offline';
  if (w.status === 'away')    return 'away';
  if (w.load_score >= 90)     return 'overloaded';
  if (w.load_score >= 70)     return 'high_load';
  return w.status; // 'available' | 'busy'
};

const getLoadBarClass = (score: number) =>
  score >= 90 ? 'bg-red-500' : score >= 70 ? 'bg-amber-400' : 'bg-emerald-500';

const getLoadTextClass = (score: number) =>
  score >= 90 ? 'text-red-600 dark:text-red-400' : score >= 70 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground';

// ─── Component ────────────────────────────────────────────────────────────────

const AgentCapacityPage: React.FC = () => {
  // ── Existing state (unchanged) ─────────────────────────────────────────────
  const [workloads, setWorkloads]               = useState<AgentWorkload[]>([]);
  const [summary, setSummary]                   = useState<WorkloadSummary | null>(null);
  const [selectedAgent, setSelectedAgent]       = useState<AgentCapacity | null>(null);
  const [isCapacityDialogOpen, setIsCapacityDialogOpen] = useState(false);
  const [loading, setLoading]                   = useState(true);

  // ── New client-side UI state (no API changes) ──────────────────────────────
  const [searchQuery, setSearchQuery]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage]                 = useState(1);
  const pageSize = 10;

  useEffect(() => { fetchData(); }, []);

  // ── Handlers — logic unchanged ─────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [workloadRes, summaryRes] = await Promise.all([
        api.get('/agent-capacity/'),
        api.get('/agent-capacity/workload/summary'),
      ]);
      setWorkloads(workloadRes.data.workloads || []);
      setSummary(summaryRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load agent capacity data.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCapacity = async (userId: number) => {
    try {
      const response = await api.get(`/agent-capacity/${userId}`);
      setSelectedAgent(response.data);
      setIsCapacityDialogOpen(true);
    } catch {
      toast({ title: 'Error', description: 'Failed to load agent settings.', variant: 'destructive' });
    }
  };

  const handleUpdateCapacity = async () => {
    if (!selectedAgent) return;
    try {
      await api.put(`/agent-capacity/${selectedAgent.user_id}`, {
        max_conversations:             selectedAgent.max_conversations,
        max_conversations_per_channel: selectedAgent.max_conversations_per_channel,
        auto_assignment_enabled:       selectedAgent.auto_assignment_enabled,
        auto_assignment_priority:      selectedAgent.auto_assignment_priority,
        working_hours_enabled:         selectedAgent.working_hours_enabled,
        working_hours:                 selectedAgent.working_hours,
        timezone:                      selectedAgent.timezone,
      });
      setIsCapacityDialogOpen(false);
      setSelectedAgent(null);
      fetchData();
      toast({ title: 'Settings saved', description: 'Agent capacity updated successfully.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update capacity settings.', variant: 'destructive' });
    }
  };

  const handleStatusUpdate = async (userId: number, status: string) => {
    try {
      await api.put(`/agent-capacity/${userId}/status`, { status });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to update agent status.', variant: 'destructive' });
    }
  };

  // ── Derived data (client-side, zero extra API calls) ──────────────────────

  // Filtered list — search + status
  const filteredWorkloads = useMemo(() => {
    let list = workloads;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (w) => w.user_name.toLowerCase().includes(q) || w.user_email.toLowerCase().includes(q),
      );
    }
    if (statusFilter && statusFilter !== 'all') {
      list = list.filter((w) => {
        const eff = getEffectiveStatus(w);
        return eff === statusFilter || w.status === statusFilter;
      });
    }
    return list;
  }, [workloads, searchQuery, statusFilter]);

  // Reset page when filters change
  useMemo(() => { setPage(1); }, [searchQuery, statusFilter]);

  // Pagination
  const totalPages         = Math.max(1, Math.ceil(filteredWorkloads.length / pageSize));
  const paginatedWorkloads = filteredWorkloads.slice((page - 1) * pageSize, page * pageSize);

  // Team Health segmented bar
  const teamHealth = useMemo(() => {
    const total = workloads.length || 1;
    return [
      { label: 'Available',  count: workloads.filter((w) => w.status !== 'offline' && w.status !== 'away' && w.load_score < 70).length, color: 'bg-emerald-500' },
      { label: 'High Load',  count: workloads.filter((w) => w.load_score >= 70 && w.load_score < 90).length,                            color: 'bg-amber-400' },
      { label: 'Overloaded', count: workloads.filter((w) => w.load_score >= 90).length,                                                  color: 'bg-red-500' },
      { label: 'Away',       count: workloads.filter((w) => w.status === 'away').length,                                                  color: 'bg-orange-400' },
      { label: 'Offline',    count: workloads.filter((w) => w.status === 'offline').length,                                               color: 'bg-muted-foreground/40' },
    ].map((g) => ({ ...g, pct: (g.count / total) * 100 }));
  }, [workloads]);

  // KPI cards config — derived from summary
  const kpiCards = useMemo(() => {
    if (!summary) return [];
    const utilRate   = summary.utilization_rate;
    const utilCrit   = utilRate >= 90;
    const utilWarn   = utilRate >= 70;
    const availPct   = summary.total_agents > 0
      ? Math.round((summary.available_agents / summary.total_agents) * 100)
      : 0;
    return [
      {
        label:   'Total Agents',
        value:   String(summary.total_agents),
        sub:     `${summary.available_agents} available right now`,
        Icon:    Users2,
        iconBg:  'bg-primary/10',
        iconFg:  'text-primary',
        valueFg: '',
      },
      {
        label:   'Available Agents',
        value:   String(summary.available_agents),
        sub:     `${availPct}% of team online`,
        Icon:    UserCheck,
        iconBg:  'bg-emerald-100',
        iconFg:  'text-emerald-600',
        valueFg: 'text-emerald-600',
      },
      {
        label:   'Total Capacity',
        value:   String(summary.total_capacity),
        sub:     `${summary.total_active_conversations} active conversations`,
        Icon:    Layers,
        iconBg:  'bg-blue-100',
        iconFg:  'text-blue-600',
        valueFg: '',
      },
      {
        label:   'Utilization Rate',
        value:   `${utilRate}%`,
        sub:     utilCrit ? 'Critical — immediate action needed'
               : utilWarn ? 'Elevated — monitor closely'
               : 'Healthy',
        Icon:    utilCrit || utilWarn ? AlertTriangle : Activity,
        iconBg:  utilCrit ? 'bg-red-100' : utilWarn ? 'bg-amber-100' : 'bg-emerald-100',
        iconFg:  utilCrit ? 'text-red-600' : utilWarn ? 'text-amber-600' : 'text-emerald-600',
        valueFg: utilCrit ? 'text-red-600' : utilWarn ? 'text-amber-600' : '',
      },
    ];
  }, [summary]);

  // Sheet agent workload (for header identity)
  const sheetAgentWorkload = useMemo(
    () => workloads.find((w) => w.user_id === selectedAgent?.user_id),
    [workloads, selectedAgent],
  );

  // ── Skeleton loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-7 w-52" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-36 rounded-md" />
          </div>
        </div>
        {/* KPI card skeletons */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="shadow-sm">
              <CardHeader>
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-9 w-9 rounded-full" />
              </CardHeader>
              <CardContent><Skeleton className="h-8 w-16" /></CardContent>
              <CardFooter><Skeleton className="h-3 w-36" /></CardFooter>
            </Card>
          ))}
        </div>
        {/* Table skeleton */}
        <Card>
          <CardHeader className="pb-0">
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="p-0 pb-2 pt-4">
            <Table>
              <TableBody>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1.5">
                          <Skeleton className="h-3.5 w-28" />
                          <Skeleton className="h-3 w-36" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-2 w-24 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-3.5 w-16" /></TableCell>
                    <TableCell className="hidden sm:table-cell"><Skeleton className="h-3.5 w-10" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-3.5 w-12" /></TableCell>
                    <TableCell className="pr-6">
                      <div className="flex justify-end gap-1">
                        <Skeleton className="h-7 w-7 rounded-md" />
                        <Skeleton className="h-7 w-7 rounded-md" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="@container/main flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agent Capacity</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Monitor and manage agent workload limits in real time
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={fetchData} aria-label="Refresh data">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              toast({ title: 'Workload rebalancing', description: 'This feature is coming soon.' })
            }
          >
            <Scale className="mr-1.5 h-3.5 w-3.5" />
            Rebalance Workload
          </Button>
        </div>
      </div>

      {/* ── KPI strip ────────────────────────────────────────────────────────── */}
      {summary && (
        <div className="grid grid-cols-1 gap-4 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
          {kpiCards.map((k) => (
            <Card
              key={k.label}
              className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm"
            >
              <CardHeader>
                <CardDescription>{k.label}</CardDescription>
                <CardAction>
                  <div className={cn('rounded-full p-2.5', k.iconBg)}>
                    <k.Icon className={cn('h-5 w-5', k.iconFg)} />
                  </div>
                </CardAction>
              </CardHeader>
              <CardContent>
                <CardTitle
                  className={cn(
                    'text-2xl font-semibold tabular-nums @[200px]/card:text-3xl',
                    k.valueFg,
                  )}
                >
                  {k.value}
                </CardTitle>
              </CardContent>
              <CardFooter>
                <p className="text-xs text-muted-foreground">{k.sub}</p>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* ── Team Health segmented bar ─────────────────────────────────────────── */}
      {workloads.length > 0 && (
        <Card className="shadow-sm">
          <CardContent className="py-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium">Team Health</p>
              <p className="text-xs text-muted-foreground">{workloads.length} agents total</p>
            </div>
            {/* Segmented bar */}
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
              {teamHealth.filter((g) => g.count > 0).map((g) => (
                <div
                  key={g.label}
                  className={cn('h-full transition-all', g.color)}
                  style={{ width: `${g.pct}%` }}
                />
              ))}
            </div>
            {/* Legend */}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
              {teamHealth.filter((g) => g.count > 0).map((g) => (
                <div key={g.label} className="flex items-center gap-1.5">
                  <span className={cn('h-2 w-2 rounded-full', g.color)} />
                  <span className="text-xs text-muted-foreground">
                    {g.label}{' '}
                    <span className="font-medium tabular-nums text-foreground">{g.count}</span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Workload table ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Agent Workload
            <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
              ({filteredWorkloads.length})
            </span>
          </CardTitle>
          <CardAction>
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search agents…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 w-40 pl-8 text-xs"
                />
              </div>
              {/* Status filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-32 text-xs">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="away">Away</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
              {/* Hover hint */}
              <span className="hidden text-xs text-muted-foreground lg:inline">
                Hover a row to reveal actions
              </span>
            </div>
          </CardAction>
        </CardHeader>

        <CardContent className="p-0 pb-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Agent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Load</TableHead>
                <TableHead>Conversations</TableHead>
                <TableHead className="hidden text-right tabular-nums sm:table-cell">
                  Resolved Today
                </TableHead>
                <TableHead className="hidden text-right tabular-nums md:table-cell">
                  Avg Response
                </TableHead>
                <TableHead className="w-20 pr-6" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedWorkloads.length === 0 ? (
                /* Empty state */
                <TableRow>
                  <TableCell colSpan={7} className="py-14 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="rounded-full bg-muted p-3">
                        <Users2 className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {searchQuery || statusFilter
                          ? 'No agents match your filters.'
                          : 'No agents loaded.'}
                      </p>
                      {(searchQuery || statusFilter) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSearchQuery(''); setStatusFilter(''); }}
                        >
                          Clear filters
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedWorkloads.map((workload) => {
                  const effStatus = getEffectiveStatus(workload);
                  const statusCfg = STATUS_CONFIG[effStatus] ?? STATUS_CONFIG.available;
                  return (
                    <TableRow key={workload.user_id} className="group">

                      {/* Agent identity */}
                      <TableCell className="pl-6">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                              {workload.user_name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{workload.user_name}</span>
                            <span className="text-xs text-muted-foreground">{workload.user_email}</span>
                          </div>
                        </div>
                      </TableCell>

                      {/* Status badge with dot */}
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn('gap-1.5 text-xs', statusCfg.badgeClass)}
                        >
                          <span className={cn('h-1.5 w-1.5 rounded-full', statusCfg.dotClass)} />
                          {statusCfg.label}
                        </Badge>
                      </TableCell>

                      {/* Load progress + percentage */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                            <div
                              className={cn(
                                'h-full rounded-full transition-all',
                                getLoadBarClass(workload.load_score),
                              )}
                              style={{ width: `${Math.min(workload.load_score, 100)}%` }}
                            />
                          </div>
                          <span
                            className={cn(
                              'text-xs font-medium tabular-nums',
                              getLoadTextClass(workload.load_score),
                            )}
                          >
                            {workload.load_score}%
                          </span>
                        </div>
                      </TableCell>

                      {/* Conversations: active / max + pending badge */}
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium tabular-nums">
                            {workload.active_conversations}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            / {workload.max_conversations}
                          </span>
                          {workload.pending_conversations > 0 && (
                            <Badge
                              variant="secondary"
                              className="h-4 px-1 text-[10px] tabular-nums"
                            >
                              +{workload.pending_conversations}
                            </Badge>
                          )}
                        </div>
                      </TableCell>

                      {/* Resolved today */}
                      <TableCell className="hidden text-right text-sm tabular-nums text-muted-foreground sm:table-cell">
                        {workload.resolved_today}
                      </TableCell>

                      {/* Avg response time */}
                      <TableCell className="hidden text-right text-sm tabular-nums text-muted-foreground md:table-cell">
                        {workload.avg_response_time_minutes}m
                      </TableCell>

                      {/* Hover-reveal actions */}
                      <TableCell className="pr-6">
                        <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          {/* Status DropdownMenu */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                aria-label="Change status"
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-36">
                              <DropdownMenuLabel className="text-xs text-muted-foreground">
                                Set Status
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {(['available', 'busy', 'away', 'offline'] as const).map((s) => (
                                <DropdownMenuItem
                                  key={s}
                                  onClick={() => handleStatusUpdate(workload.user_id, s)}
                                >
                                  <span
                                    className={cn(
                                      'mr-2 h-1.5 w-1.5 rounded-full',
                                      STATUS_CONFIG[s].dotClass,
                                    )}
                                  />
                                  {STATUS_CONFIG[s].label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {/* Capacity settings — opens Sheet */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleEditCapacity(workload.user_id)}
                            aria-label={`Configure ${workload.user_name}`}
                          >
                            <Settings2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>

                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination — only rendered when > 1 page */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-6 py-3">
              <p className="text-xs text-muted-foreground">
                Showing{' '}
                <span className="font-medium tabular-nums">
                  {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredWorkloads.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium tabular-nums">{filteredWorkloads.length}</span>
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(1)} disabled={page === 1} aria-label="First page">
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p - 1)} disabled={page === 1} aria-label="Previous page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} aria-label="Next page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(totalPages)} disabled={page === totalPages} aria-label="Last page">
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Capacity Settings — Sheet (slide-over, preserves table context) ──── */}
      <Sheet
        open={isCapacityDialogOpen}
        onOpenChange={(open) => {
          if (!open) { setIsCapacityDialogOpen(false); setSelectedAgent(null); }
        }}
      >
        <SheetContent side="right" className="flex flex-col gap-0 p-0 sm:max-w-lg">

          {/* Sheet header — agent identity */}
          <SheetHeader className="border-b px-6 py-4 pr-14">
            {sheetAgentWorkload ? (
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                    {sheetAgentWorkload.user_name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <SheetTitle className="text-base">{sheetAgentWorkload.user_name}</SheetTitle>
                  <SheetDescription className="text-xs">{sheetAgentWorkload.user_email}</SheetDescription>
                </div>
              </div>
            ) : (
              <SheetTitle>Capacity Settings</SheetTitle>
            )}
          </SheetHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {selectedAgent && (
              <div className="space-y-6">

                {/* Current workload — mini KPI cards */}
                {selectedAgent.current_workload && (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Active',     value: selectedAgent.current_workload.active_conversations,  Icon: MessageSquare },
                      { label: 'Pending',    value: selectedAgent.current_workload.pending_conversations, Icon: Clock },
                      { label: 'Load Score', value: `${selectedAgent.current_workload.load_score}%`,      Icon: Activity },
                    ].map(({ label, value, Icon }) => (
                      <Card key={label} className="bg-muted/40 shadow-none">
                        <CardContent className="px-3 py-2.5">
                          <div className="mb-1 flex items-center gap-1.5">
                            <Icon className="h-3 w-3 text-muted-foreground" />
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {label}
                            </p>
                          </div>
                          <p className="text-xl font-semibold tabular-nums">{value}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Numeric settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="max-conv" className="text-xs font-medium">
                      Max Conversations
                    </Label>
                    <Input
                      id="max-conv"
                      type="number"
                      min={1}
                      value={selectedAgent.max_conversations}
                      onChange={(e) =>
                        setSelectedAgent({
                          ...selectedAgent,
                          max_conversations: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="priority" className="text-xs font-medium">
                      Assignment Priority
                    </Label>
                    <Input
                      id="priority"
                      type="number"
                      min={1}
                      value={selectedAgent.auto_assignment_priority}
                      onChange={(e) =>
                        setSelectedAgent({
                          ...selectedAgent,
                          auto_assignment_priority: parseInt(e.target.value) || 1,
                        })
                      }
                    />
                    <p className="text-[11px] text-muted-foreground">Lower = higher priority</p>
                  </div>
                </div>

                <Separator />

                {/* Toggle switches */}
                <div className="space-y-3">
                  {(
                    [
                      {
                        id:    'auto-assign',
                        label: 'Auto Assignment',
                        desc:  'Automatically assign incoming conversations to this agent',
                        key:   'auto_assignment_enabled' as const,
                      },
                      {
                        id:    'working-hrs',
                        label: 'Working Hours',
                        desc:  'Restrict availability to scheduled working hours only',
                        key:   'working_hours_enabled' as const,
                      },
                    ] as const
                  ).map(({ id, label, desc, key }) => (
                    <div
                      key={id}
                      className="flex items-center justify-between rounded-lg border px-4 py-3"
                    >
                      <div className="space-y-0.5">
                        <Label htmlFor={id} className="cursor-pointer text-sm font-medium">
                          {label}
                        </Label>
                        <p className="text-xs text-muted-foreground">{desc}</p>
                      </div>
                      <Switch
                        id={id}
                        checked={selectedAgent[key]}
                        onCheckedChange={(checked) =>
                          setSelectedAgent({ ...selectedAgent, [key]: checked })
                        }
                      />
                    </div>
                  ))}
                </div>

              </div>
            )}
          </div>

          {/* Footer actions */}
          <SheetFooter className="gap-2 border-t px-6 py-4">
            <Button
              variant="outline"
              onClick={() => { setIsCapacityDialogOpen(false); setSelectedAgent(null); }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateCapacity}>Save Changes</Button>
          </SheetFooter>

        </SheetContent>
      </Sheet>

    </div>
  );
};

export default AgentCapacityPage;
